import type { ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback } from "react";
import classNames from "classnames";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { defaultStyles } from "./defaultStyles";

export interface LinkProps extends ComponentPropsWithRef<"a"> {
  "data-preview-mode"?: boolean;
  href?: string;
  styles: Record<string, StyleValue>;
  text: string;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      text,
      href,
      className,
      "data-preview-mode": isPreview,
      onClick,
      styles,
      rel = "noopener noreferrer",
      target = "_self",
      ...rest
    },
    ref,
  ) => {
    const inlineStyles = parseStyles({ styles, defaultStyles });

    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isPreview) {
          event.preventDefault();
        }

        if (onClick) {
          onClick(event);
        }
      },
      [isPreview, onClick],
    );

    return (
      <a
        ref={ref}
        href={href}
        className={classNames(
          "wb-text-link wb-font-p1 wb-text-p1 wb-leading-p1 wb-tracking-p1 wb-block wb-w-full wb-underline",
          className,
        )}
        onClick={handleClick}
        style={inlineStyles}
        rel={rel}
        target={target}
        {...rest}
      >
        {text}
      </a>
    );
  },
);

Link.displayName = "Link";
